import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import profileImage from '../assets/images/jason.jpg';

function Home() {
  const location = useLocation();
  const [uiRepoLastUpdated, setUiRepoLastUpdated] = useState('Loading...');
  const [apiRepoLastUpdated, setApiRepoLastUpdated] = useState('Loading...');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle hash navigation for smooth scrolling to sections
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        // Small delay to ensure page is fully loaded
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  useEffect(() => {
    const fetchRepoUpdateDates = async () => {
      try {
        // Fetch last commit date for the repository
        const response = await fetch(
          'https://api.github.com/repos/schmitty890/jasonschmitt/commits/master',
        );
        const data = await response.json();

        if (data.commit && data.commit.committer && data.commit.committer.date) {
          const commitDate = new Date(data.commit.committer.date);
          const formattedDate = commitDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });

          // Since frontend and backend are in the same repo, use the same date
          setUiRepoLastUpdated(formattedDate);
          setApiRepoLastUpdated(formattedDate);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching repo update dates:', error);
        setUiRepoLastUpdated('Recently');
        setApiRepoLastUpdated('Recently');
      }
    };

    fetchRepoUpdateDates();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      await axios.post(`${apiUrl}/api/messages`, formData);

      setFormStatus({
        type: 'success',
        message: 'Thank you! Your message has been sent successfully.',
      });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to send message. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <section id="home" className="bg-gray-900 py-20">
        <div className="flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
          <h1 className="text-6xl font-bold mb-4 text-center">
            <span className="text-white">Hello,</span>
            <br />
            <span className="text-white">This is </span>
            <span className="text-purple-400">Jason Schmitt</span>
            <br />
            <span className="text-white">a </span>
            <span className="text-yellow-400">Software Engineer.</span>
          </h1>
          <div className="flex space-x-6 mb-8">
            <a
              href="https://github.com/schmitty890"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 transition-colors"
            >
              <span className="sr-only">GitHub</span>
              <svg className="h-12 w-12" fill="currentColor" viewBox="0 0 24 24">
                <path
                  fillRule="evenodd"
                  d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/in/jasonlschmitt/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 transition-colors"
            >
              <span className="sr-only">LinkedIn</span>
              <svg className="h-12 w-12" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                />
              </svg>
            </a>
          </div>
          <button
            type="button"
            onClick={scrollToContact}
            className="border-2 border-cyan-400 text-cyan-400 px-8 py-3 rounded-lg font-semibold hover:bg-cyan-400 hover:text-gray-900 transition-colors"
          >
            CONTACT ME
          </button>
        </div>
      </section>

      {/* WHO AM I Section */}
      <section className="bg-gray-900 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-4xl font-bold text-cyan-400 mb-6">
                WHO AM I?
              </h1>
              <p className="text-white text-lg mb-6 leading-relaxed">
                I&apos;m a Software Engineer specializing in full stack web development with
                JavaScript and Node. Based out of Charlotte NC region, I&apos;m currently
                employed by
                {' '}
                <a
                  href="https://www.bankofamerica.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  Bank of America
                </a>
                {' '}
                building a front end UI framework.
              </p>
            </div>
            <div className="flex justify-center md:justify-end">
              <img
                src={profileImage}
                alt="Jason Schmitt"
                className="w-64 h-64 rounded-full object-cover border-4 border-cyan-400"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-cyan-400 mb-8">About</h2>
          <div className="space-y-6 text-white text-lg leading-relaxed">
            <p>
              Hi, I&apos;m Jason. A North Carolina based front end engineer who
              recently completed a Masters in Information Technology concentrating
              in Web Development from UNC Charlotte. I am excited about
              progressing my career in full stack web development as well as
              learning more about website architecture.
            </p>
            <p>
              This site also acts as a playground for my UI and API projects. The
              {' '}
              <a
                href="https://github.com/schmitty890/jasonschmitt/tree/master/client"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                UI repo
              </a>
              {' '}
              was last updated
              {' '}
              <span className="font-semibold text-purple-400">
                {uiRepoLastUpdated}
              </span>
              , and is hosted on Netlify. The
              {' '}
              <a
                href="https://github.com/schmitty890/jasonschmitt/tree/master/server"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                API repo
              </a>
              {' '}
              was last updated
              {' '}
              <span className="font-semibold text-purple-400">
                {apiRepoLastUpdated}
              </span>
              . I enjoy tinkering and testing out projects and new
              node modules with this playground site, that also acts as a
              portfolio site.
            </p>
            <p>
              One of the biggest opportunities working with a fortune 500 company
              is the size and number of teams to work with. My core responsibility
              involved building a global styleguide for our internal application
              teams which grew into an entire front-end framework involving the
              dynamic creation of components, modules, utilities, helpers, and
              more.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Projects Section - Commented out for future use */}
      {/* <section id="projects" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Featured Projects
          </h2>
          <div className="grid gap-8 md:grid-cols-2">
            <div
              className="bg-white rounded-lg shadow-md overflow-hidden
                hover:shadow-lg transition-shadow"
            >
              <div className="h-48 bg-gradient-to-br from-blue-500 to-blue-700" />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Project One
                </h3>
                <p className="text-gray-600 mb-4">
                  A full-stack MERN application with authentication and real-time
                  features. Built with React, Node.js, Express, and MongoDB.
                </p>
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-700 font-semibold"
                >
                  View Project →
                </button>
              </div>
            </div>

            <div
              className="bg-white rounded-lg shadow-md overflow-hidden
                hover:shadow-lg transition-shadow"
            >
              <div className="h-48 bg-gradient-to-br from-purple-500 to-purple-700" />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Project Two
                </h3>
                <p className="text-gray-600 mb-4">
                  An open-source tool for developers featuring modern UI and
                  efficient workflows. Deployed with CI/CD pipeline.
                </p>
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-700 font-semibold"
                >
                  View Project →
                </button>
              </div>
            </div>

            <div
              className="bg-white rounded-lg shadow-md overflow-hidden
                hover:shadow-lg transition-shadow"
            >
              <div className="h-48 bg-gradient-to-br from-green-500 to-green-700" />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Project Three
                </h3>
                <p className="text-gray-600 mb-4">
                  A responsive web application with modern design and seamless user
                  experience. Optimized for performance.
                </p>
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-700 font-semibold"
                >
                  View Project →
                </button>
              </div>
            </div>

            <div
              className="bg-white rounded-lg shadow-md overflow-hidden
                hover:shadow-lg transition-shadow"
            >
              <div className="h-48 bg-gradient-to-br from-orange-500 to-orange-700" />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Project Four
                </h3>
                <p className="text-gray-600 mb-4">
                  Cloud-native application with microservices architecture and
                  containerization. Built for scalability.
                </p>
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-700 font-semibold"
                >
                  View Project →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Contact Section */}
      <section id="contact" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-cyan-400 mb-6">Contact</h2>
          <p className="text-white mb-8">
            What would you like to discuss?
          </p>
          {formStatus.message && (
            <div
              className={`mb-6 p-4 rounded-lg ${
                formStatus.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {formStatus.message}
            </div>
          )}
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                Name
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="mt-2 w-full px-4 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
                  placeholder="Your name"
                />
              </label>
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                Email
                {' '}
                <span className="text-red-400">*</span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="mt-2 w-full px-4 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
                  placeholder="your.email@example.com"
                />
              </label>
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                Message
                {' '}
                <span className="text-red-400">*</span>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  className="mt-2 w-full px-4 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
                  placeholder="Your message..."
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full px-6 py-3 rounded-lg font-semibold transition-colors ${
                isSubmitting
                  ? 'bg-cyan-500 cursor-not-allowed'
                  : 'bg-cyan-400 hover:bg-cyan-500 text-gray-900'
              }`}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

export default Home;
